import { Map as MapLibreMap, NavigationControl, GeolocateControl, ScaleControl, AttributionControl } from 'maplibre-gl';
import { createRadar } from './map/radar.js';
import { createRelief3d } from './map/relief3d.js';
import { buildStyle } from './map/style.js';
import { createFilterPanel } from './ui/filterPanel.js';
import { createFavoritesPanel } from './ui/favoritesPanel.js';
import { createRoutePanel } from './ui/routePanel.js';
import { createNavOverlay } from './ui/navOverlay.js';
import { createSearchBox } from './ui/searchBox.js';
import { createAccount } from './ui/account.js';
import { createInfoPopup } from './map/popup.js';
import { backend } from './data/backend.js';
import { toast } from './ui/toast.js';
import './styles/app.css';

const DEFAULT_VIEW = { center: [8.2275, 46.8], zoom: 7.4 };

/* ------------------------------------------------------- Ansicht in der URL */

function viewFromHash() {
  const m = location.hash.match(/#map=([\d.]+)\/(-?[\d.]+)\/(-?[\d.]+)/);
  if (!m) return null;
  const [, z, lat, lng] = m;
  return { center: [Number(lng), Number(lat)], zoom: Number(z) };
}

function writeHash(map) {
  const c = map.getCenter();
  const hash = `#map=${map.getZoom().toFixed(2)}/${c.lat.toFixed(5)}/${c.lng.toFixed(5)}`;
  history.replaceState(null, '', hash);
}

/* ------------------------------------------------------------------- Karte */

const view = viewFromHash() || DEFAULT_VIEW;

const map = new MapLibreMap({
  container: 'map',
  style: buildStyle(),
  center: view.center,
  zoom: view.zoom,
  maxZoom: 19,
  hash: false,
  attributionControl: false,
  dragRotate: false,
  pitchWithRotate: false
});

map.touchZoomRotate.disableRotation();
map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
map.addControl(new GeolocateControl({ trackUserLocation: true, positionOptions: { enableHighAccuracy: true } }), 'bottom-right');
map.addControl(new ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-left');
map.addControl(new AttributionControl({ compact: true }), 'bottom-left');

map.on('moveend', () => writeHash(map));

// Im Entwicklungsmodus greifbar machen - erleichtert das Nachsehen in der Konsole
if (import.meta.env?.DEV) window.map = map;

/* ---------------------------------------------------------- Kopfzeile / UI */

const el = (id) => document.getElementById(id);

const panel = el('panel');
const panelBtn = el('btn-panel');
const backdrop = el('panel-backdrop');

/** Schmales Fenster: das Panel liegt über der Karte statt daneben. */
const compact = () => window.matchMedia('(max-width: 860px)').matches;

function setPanel(open) {
  panel.hidden = !open;
  panelBtn.setAttribute('aria-pressed', String(open));
  backdrop.hidden = !open || !compact();
  if (!open) for (const b of navButtons) b.classList.remove('is-active');
  map.resize();
}

/** Nach einer Auswahl soll auf dem Telefon wieder die Karte zu sehen sein. */
function collapseOnCompact() {
  if (compact()) setPanel(false);
}
const views = {
  filters: el('view-filters'),
  route: el('view-route'),
  favorites: el('view-favorites')
};
const navButtons = [el('btn-filters'), el('btn-route'), el('btn-favorites')];

function showView(name) {
  for (const [key, node] of Object.entries(views)) node.hidden = key !== name;
  for (const b of navButtons) b.classList.toggle('is-active', b.dataset.view === name);
  setPanel(true);
}

for (const b of navButtons) {
  b.addEventListener('click', () => {
    const alreadyOpen = !panel.hidden && b.classList.contains('is-active');
    if (alreadyOpen) setPanel(false);
    else showView(b.dataset.view);
  });
}

// Auf dem Telefon startet die Karte frei - das Panel holt man sich bei Bedarf.
setPanel(!compact());
if (!compact()) el('btn-filters').classList.add('is-active');

panelBtn.addEventListener('click', () => setPanel(panel.hidden));
backdrop.addEventListener('click', () => setPanel(false));
el('panel-close').addEventListener('click', () => setPanel(false));
map.on('click', collapseOnCompact);
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !panel.hidden && compact()) setPanel(false);
});

el('btn-share').addEventListener('click', async () => {
  writeHash(map);
  try {
    await navigator.clipboard.writeText(location.href);
    toast('Link zur Ansicht kopiert.');
  } catch {
    toast(location.href);
  }
});

/* --------------------------------------------------- Konto, Favoriten, Filter */

const account = createAccount({
  button: el('btn-account'),
  onChange: () => {
    favorites.load();
    loadPrefs();
  }
});

const favorites = createFavoritesPanel({
  map,
  host: el('fav-list'),
  badge: el('fav-count'),
  modeEl: el('fav-mode'),
  backend,
  getProfile: () => account.getProfile(),
  onNavigate: collapseOnCompact
});

/**
 * Palettenwechsel lädt den Style neu. `diff: true` hält die Kacheln im Speicher,
 * aber Sichtbarkeiten und eigene Ebenen (Route) sind danach weg - beides wird
 * hier wieder aufgetragen.
 */
let renderedPalette = 'farbig';
function usePalette(palette) {
  if (!palette || palette === renderedPalette) return;
  renderedPalette = palette;
  map.setStyle(buildStyle({ palette }), { diff: true });
  map.once('styledata', () => {
    filters.reapply();
    route.redraw();
    radar.reapply();
    relief3d.reapply();
  });
}

const filters = createFilterPanel({
  map,
  onChange: () => savePrefsSoon(),
  onPalette: usePalette,
  // "Alle zeigen" im Filterfeld räumt auch das Suchfeld auf.
  onKategorieWeg: () => search?.leeren()
});

// Zielfuehrung blickt schraeg nach vorn - ohne Gelaende wirkt das wie eine
// umgelegte Tapete. Danach den vorherigen Zustand wiederherstellen.
let dreiDVorher = false;
const navOverlay = createNavOverlay({
  map,
  onStart: () => {
    dreiDVorher = relief3d.istAn();
    relief3d.setOn(true, { stillLassen: true });
  },
  onEnd: () => {
    relief3d.setOn(dreiDVorher, { stillLassen: true });
    setPanel(!compact());
  }
});

const route = createRoutePanel({
  map,
  onActivate: () => showView('route'),
  onStartNavigation: (auftrag) => {
    setPanel(false);
    navOverlay.starten(auftrag);
  }
});
const radar = createRadar({ map });
const relief3d = createRelief3d({ map });

// Beim Setzen eines Routenpunkts aus der Karte heraus nicht sofort zuklappen -
// die Liste der Verbindungen ist ja das Ergebnis.

let prefsTimer;
function savePrefsSoon() {
  clearTimeout(prefsTimer);
  prefsTimer = setTimeout(async () => {
    try {
      await backend.prefs.save(account.getProfile(), { filters: filters.getState() });
    } catch { /* Einstellungen sind nicht kritisch */ }
  }, 600);
}

async function loadPrefs() {
  try {
    const prefs = await backend.prefs.load(account.getProfile());
    if (prefs?.filters) filters.setState(prefs.filters);
  } catch { /* Standardfilter behalten */ }
}

radar.mount(el('radar'));
relief3d.mount(el('relief3d'));

filters.mount({
  basemapHost: el('basemap'),
  groupHost: el('layer-groups'),
  themeHost: el('poi-themes'),
  nameInput: el('poi-name'),
  resetBtn: el('btn-reset'),
  poiNoneBtn: el('btn-poi-none')
});

route.mount({
  modeHost: el('route-modes'),
  fromInput: el('route-from'),
  toInput: el('route-to'),
  swapBtn: el('route-swap'),
  clearBtn: el('route-clear'),
  result: el('route-result')
});

createInfoPopup({
  map,
  onFavorite: (fav) => favorites.add(fav),
  onRoutePoint: (which, point) => route.setPoint(which, point)
});

const search = createSearchBox({
  map,
  input: el('search-input'),
  clearBtn: el('search-clear'),
  resultList: el('search-results'),
  onPick: collapseOnCompact,
  onKategorie: (kat) => filters.setKategorie(kat)
});

(async () => {
  await account.restore();
  await favorites.load();
  await loadPrefs();
})();

map.on('error', (e) => {
  // Reliefkacheln decken nur die Schweiz ab – fehlende Kacheln sind normal.
  const url = e?.error?.url || '';
  if (url.includes('wmts.geo.admin.ch')) return;
  console.warn('Kartenfehler:', e?.error?.message || e);
});

document.addEventListener('keydown', (e) => {
  if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
    e.preventDefault();
    el('search-input').focus();
  }
});
